import {
  collection,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  startAfter,
  where,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { addDocument } from '../firebase-utils';
import { Checkin } from '../types/checkin-types';
import { CheckinsCollection, CheckinEntriesCollection } from '../collections';
import { auth, firestoreDb } from '../client';

export interface SaveCheckinInput {
  locationId: string;
  name: string;
  email: string;
  optIn?: boolean;
}

export const saveCheckin = async (
  input: SaveCheckinInput,
): Promise<{ success: boolean; id?: string }> => {
  const { locationId, name, email, optIn } = input;
  if (!locationId || !name || !email) {
    return { success: false };
  }

  const path = `${CheckinsCollection}/${locationId}/${CheckinEntriesCollection}`;
  const data: Checkin = {
    locationId,
    name,
    email,
    optIn,
    createdAt: Date.now(),
  };

  const result = await addDocument(
    data as unknown as Record<string, unknown>,
    path,
  );
  return { success: !!result?.documentId, id: result?.documentId };
};

type FirestoreTimestamp = {
  seconds?: number;
  nanoseconds?: number;
  toMillis?: () => number;
};

const normalizeTimestamp = (value: unknown): number => {
  if (typeof value === 'number') return value;
  const ts = value as FirestoreTimestamp | undefined;
  if (ts?.toMillis) {
    return ts.toMillis() ?? Date.now();
  }
  if (typeof ts?.seconds === 'number') {
    const nanos = typeof ts.nanoseconds === 'number' ? ts.nanoseconds : 0;
    return ts.seconds * 1000 + nanos / 1_000_000;
  }
  return Date.now();
};

const normalizeCheckin = (
  raw: Record<string, unknown>,
  id?: string,
): Checkin => {
  const parseOptIn = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(v)) return true;
      if (['false', '0', 'no', 'n', 'off'].includes(v)) return false;
    }
    return false;
  };

  return {
    key: (raw.key as string) || id,
    locationId: (raw.locationId as string) || '',
    name: (raw.name as string) || '',
    email: (raw.email as string) || '',
    optIn: parseOptIn(raw.optIn),
    createdAt: normalizeTimestamp(raw.createdAt),
  };
};

export const fetchCheckinsForLocation = async (
  locationId?: string,
): Promise<Checkin[]> => {
  if (!locationId) return [];
  const currentUser = auth.currentUser;
  if (!currentUser) return [];

  try {
    const snapshot = await getDocs(
      collection(
        firestoreDb,
        `${CheckinsCollection}/${locationId}/${CheckinEntriesCollection}`,
      ),
    );
    return snapshot.docs
      .map(doc =>
        normalizeCheckin(doc.data() as Record<string, unknown>, doc.id),
      )
      .sort((a, b) => b.createdAt - a.createdAt);
  } catch (_e) {
    return [];
  }
};

export interface CheckinsPageResult {
  items: Checkin[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export interface FetchCheckinsPageOptions {
  limit?: number;
  cursor?: QueryDocumentSnapshot<DocumentData> | null;
  optInOnly?: boolean;
}

export const fetchCheckinsPage = async (
  locationId: string,
  options: FetchCheckinsPageOptions = {},
): Promise<CheckinsPageResult> => {
  const { limit = 30, cursor, optInOnly } = options;
  const currentUser = auth.currentUser;
  if (!currentUser || !locationId) {
    return { items: [], cursor: null, hasMore: false };
  }

  try {
    const constraints = [
      orderBy('createdAt', 'desc'),
      ...(optInOnly ? [where('optIn', '==', true)] : []),
      fbLimit(limit),
      ...(cursor ? [startAfter(cursor)] : []),
    ];
    const q = query(
      collection(
        firestoreDb,
        `${CheckinsCollection}/${locationId}/${CheckinEntriesCollection}`,
      ),
      ...constraints,
    );

    const snapshot = await getDocs(q);
    const docs = snapshot.docs;
    const items = docs.map(doc =>
      normalizeCheckin(doc.data() as Record<string, unknown>, doc.id),
    );
    const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;

    return {
      items,
      cursor: lastDoc,
      hasMore: docs.length === limit,
    };
  } catch (_e) {
    return { items: [], cursor: null, hasMore: false };
  }
};

export interface PromotionSendInput {
  locationId: string;
  recipients: Array<Pick<Checkin, 'name' | 'email'>>;
  subject?: string;
  message: string;
}

export const sendPromotionToCheckins = async (
  input: PromotionSendInput,
): Promise<{ success: boolean; count: number }> => {
  const { locationId, recipients, message } = input;
  if (!locationId || !recipients.length || !message.trim()) {
    return { success: false, count: 0 };
  }

  try {
    const functions = getFunctions();
    const callable = httpsCallable<
      PromotionSendInput,
      { success: boolean; count: number }
    >(functions, 'sendCheckinPromotion');
    const result = await callable(input);
    return {
      success: !!result.data?.success,
      count: result.data?.count ?? 0,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to send promotion via SendGrid', e);
    return { success: false, count: 0 };
  }
};
